import React, { useRef, useEffect, forwardRef, useImperativeHandle, useState } from 'react';
import { Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useTranslation } from 'react-i18next';

interface MessageInputProps {
  message?: string;
  setMessage?: (message: string) => void;
  handleSend?: (e: React.FormEvent) => void;
  onSendMessage?: (text: string) => void;
  isLoading?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
}

const MessageInput = forwardRef<{ focus: () => void }, MessageInputProps>(({
  message: externalMessage,
  setMessage: setExternalMessage,
  handleSend,
  onSendMessage,
  isLoading = false,
  disabled = false,
  autoFocus = false
}, ref) => {
  const { t } = useTranslation();
  // Internal state for when external message is not provided
  const [internalMessage, setInternalMessage] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  
  // Either use external state or internal state
  const message = externalMessage !== undefined ? externalMessage : internalMessage;
  const setMessage = setExternalMessage ? setExternalMessage : setInternalMessage;
  
  // Expose focus method
  useImperativeHandle(ref, () => ({
    focus: () => {
      inputRef.current?.focus();
    }
  }));

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (message.trim()) {
        if (handleSend) {
          handleSend(e as unknown as React.FormEvent);
        } else if (onSendMessage) {
          onSendMessage(message);
          setInternalMessage(''); // Only clear internal state
        }
      }
    }
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim()) {
      if (handleSend) {
        handleSend(e);
      } else if (onSendMessage) {
        onSendMessage(message);
        setInternalMessage(''); // Only clear internal state
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="border-t p-3 bg-white flex items-end gap-2">
      <Textarea
        ref={inputRef}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={t('chatbot.typeMessage')}
        className="min-h-[40px] max-h-[120px] resize-none text-sm"
        disabled={isLoading || disabled}
      />
      <Button 
        type="submit" 
        size="icon" 
        disabled={!message.trim() || isLoading || disabled} 
        className="h-10 w-10 rounded-full bg-gradient-to-r from-gebeya-pink to-gebeya-orange text-white hover:opacity-90 disabled:opacity-50 flex-shrink-0"
      >
        <Send className="h-5 w-5" />
      </Button>
    </form>
  );
});

MessageInput.displayName = 'MessageInput';

export default MessageInput; 